import {
  Button,
  TextInput,
  Dialog,
  LoadingSpinner,
  PlainButton,
} from "@jugl-web/ui-kit";
import { useTranslations } from "@jugl-web/utils";
import { normalizeShopifyStoreDomain } from "@web-src/modules/shopify/utils/shopifyStoreDomain";
import React, { useMemo, useState } from "react";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as ShopifyIcon } from "./assets/shopify.svg";
import { ReactComponent as ArrowRightIcon } from "./assets/arrow-right.svg";
import { ReactComponent as SettingsIcon } from "./assets/settings.svg";

export interface ShopifyStorePromptDialogProps {
  isOpen: boolean;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (store: string) => void;
}

export const ShopifyStorePromptDialog: React.FC<
  ShopifyStorePromptDialogProps
> = ({ isOpen, isSubmitting, onClose, onSubmit }) => {
  const [store, setStore] = useState("");

  const { t } = useTranslations();

  const normalizedStore = useMemo(
    () => normalizeShopifyStoreDomain(store),
    [store]
  );

  const handleClose = () => {
    setStore("");
    onClose();
  };

  // Keep the entered value: the dialog stays open when the submit is rejected
  // (e.g. the store is already linked to another workspace).
  const handleContinue = () => {
    if (!normalizedStore) return;
    onSubmit(normalizedStore);
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      className="flex w-full max-w-[780px] flex-col overflow-hidden rounded-2xl"
    >
      <div className="flex h-[60px] shrink-0 items-center justify-between border-0 border-b border-solid border-dark-100 px-6">
        <div className="flex items-center gap-3">
          <SettingsIcon />
          <span className="font-secondary font-semibold text-[#383838]">
            {t({
              id: "common.settings",
              defaultMessage: "Settings",
            })}
          </span>
        </div>
        <PlainButton
          onClick={handleClose}
          className="flex items-center justify-center"
        >
          <CloseIcon />
        </PlainButton>
      </div>
      <div className="flex flex-col gap-8 px-14 py-10">
        <div className="flex items-center gap-2">
          <ShopifyIcon />
          <span className="font-secondary text-[22px] font-semibold text-[#1A2023]">
            {t({
              id: "cx-actions-page.shopify",
              defaultMessage: "Shopify",
            })}
          </span>
        </div>
        <div className="flex items-stretch gap-4">
          <div className="w-px shrink-0 self-stretch rounded-full bg-[#E0E0E0]" />
          <span className="font-secondary text-[13px] leading-[150%] text-dark-600">
            {t(
              {
                id: "cx-actions-page.shopify-store-prompt-helper",
                defaultMessage:
                  "Enter your original Shopify domain name.{br}You can find it in your Shopify Admin under Settings → Domains",
              },
              { br: <br /> }
            )}
          </span>
        </div>
        <TextInput
          value={store}
          onChange={(event) => setStore(event.target.value.trimStart())}
          classNames={{
            startContentWrapperClassName: "bg-white text-dark-500",
            endContentWrapperClassName: "bg-white text-dark-500",
          }}
          placeholder={t({
            id: "cx-actions-page.enter-domain-name",
            defaultMessage: "Enter domain name",
          })}
          startContent="http://"
          endContent=".myshopify.com/"
        />
      </div>
      <div className="flex justify-end border-0 border-t border-solid border-dark-100 px-6 py-4">
        <Button
          onClick={handleContinue}
          isDisabled={!normalizedStore || isSubmitting}
          iconEnd={
            isSubmitting ? (
              <LoadingSpinner size="xs" color="grey" />
            ) : (
              <ArrowRightIcon />
            )
          }
          className="w-[300px]"
        >
          {t({
            id: "common.submit",
            defaultMessage: "Submit",
          })}
        </Button>
      </div>
    </Dialog>
  );
};
